import React, { useEffect, useState } from 'react';
import {
  alpha,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AutoAwesome,
  BadgeOutlined,
  ChevronLeftRounded,
  ChevronRightRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  DashboardRounded,
  ExploreRounded,
  FlightTakeoffRounded,
  GroupRounded,
  HandshakeRounded,
  InsightsRounded,
  LanguageRounded,
  LogoutRounded,
  LoyaltyRounded,
  MapRounded,
  ModeOfTravelRounded,
  RateReviewRounded,
  SupportAgentRounded,
  TravelExploreRounded
} from '@mui/icons-material';
import { NavLink, useLocation } from 'react-router-dom';

const drawerWidth = 292;
const collapsedWidth = 92;
const logoUrl = 'https://aaryanholidays.com/static/media/logo.c1413be8b4666faa097349a8205e293c.svg';

const menuSections = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', icon: DashboardRounded, to: '/' }
    ]
  },
  {
    label: 'Tour',
    items: [
      { label: 'Manage Tour', icon: TravelExploreRounded, to: '/manage-continents' },
      { label: 'Discovery', icon: ExploreRounded, to: '/discovery/aaryan_discovery/Discovery/landscape' }
    ]
  },
  {
    label: 'Home',
    items: [
      { label: 'Exclusive Partners', icon: HandshakeRounded, to: '/manage-partners' },
      { label: 'Core Competency', icon: AutoAwesome, to: '/manage-core-competency' },
      { label: 'Home Services', icon: LoyaltyRounded, to: '/edit-table/aaryan_home_services/Home Services/landscape' },
      { label: 'Why Choose Us', icon: InsightsRounded, to: '/edit-table/aaryan_home_why_choose/Why Choose Us/landscape' },
      { label: 'Home Top Bar', icon: SupportAgentRounded, to: '/edit-table/aaryan_home_topbar/Home Top Bar/potrait' },
      { label: 'Testimonial', icon: RateReviewRounded, to: '/table/aaryan_testimonial/Testimonial/landscape' },
      { label: 'Home Banner', icon: FlightTakeoffRounded, to: '/home-mantle' },
      { label: 'Accrediation', icon: BadgeOutlined, to: '/manage-accrediation' },
    ]
  },
  {
    label: 'About',
    items: [
      { label: 'About Data', icon: LanguageRounded, to: '/table/aaryaan_about/About/potrait' },
      { label: 'About Achievements', icon: InsightsRounded, to: '/table/aaryan_about/About Achievements/potrait' }
    ]
  },
  {
    label: 'Operations',
    items: [
      { label: 'Contact', icon: SupportAgentRounded, to: '/table/aaryan_contact/Contact/landscape' },
      { label: 'User Data', icon: GroupRounded, to: '/table/aaryan_users/User Data/landscape' },
      { label: 'Travel Data', icon: ModeOfTravelRounded, to: '/table/travel_form_submissions/Travel Data/landscape' },
      { label: 'Tour Enquiry', icon: MapRounded, to: '/table/aaryan_itenary_user_data/Tour Enquiry/landscape' }
    ]
  }
];

const Sidebar = ({ open, mobileOpen, isMobile, onCloseMobile, onToggleDesktop, onLogout }) => {
  const location = useLocation();
  const drawerOpen = isMobile ? mobileOpen : true;
  const getInitialExpanded = () => {
    const state = {};
    menuSections.forEach((section) => {
      state[section.label] = section.items.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
    });
    return state;
  };
  const [expandedSections, setExpandedSections] = useState(getInitialExpanded);

  useEffect(() => {
    setExpandedSections((current) => {
      const next = { ...current };
      menuSections.forEach((section) => {
        if (section.items.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`))) {
          next[section.label] = true;
        }
      });
      return next;
    });
  }, [location.pathname]);

  const toggleSection = (label) => {
    setExpandedSections((current) => ({
      ...current,
      [label]: !current[label]
    }));
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #07162d 0%, #0a1d3d 36%, #0d2d64 100%)',
        color: 'common.white'
      }}
    >
      <Box
        sx={{
          px: open ? 3 : 2,
          pt: 3,
          pb: 2.5,
          borderBottom: `1px solid ${alpha('#ffffff', 0.08)}`
        }}
      >
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent={open ? 'space-between' : 'center'}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent={open ? 'flex-start' : 'center'}>
              <Box
                sx={{
                  width: open ? 52 : 44,
                  height: open ? 52 : 44,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'linear-gradient(135deg, rgba(16, 185, 255, 0.2) 0%, rgba(53, 125, 255, 0.2) 100%)',
                  boxShadow: '0 14px 34px rgba(19, 111, 255, 0.22)',
                  border: '1px solid rgba(125, 211, 252, 0.2)',
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={logoUrl}
                  alt="Aaryan Holidays logo"
                  sx={{
                    width: open ? 34 : 28,
                    height: open ? 34 : 28,
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)'
                  }}
                />
              </Box>
              {open && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: alpha('#7dd3fc', 0.92), letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700 }}>
                    Aaryan Holidays
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                    Admin Studio
                  </Typography>
                </Box>
              )}
            </Stack>
          {!isMobile && (
            <IconButton
              onClick={onToggleDesktop}
              sx={{
                width: 34,
                height: 34,
                color: '#e0f2fe',
                background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.22) 0%, rgba(37, 99, 235, 0.26) 100%)',
                boxShadow: '0 10px 30px rgba(8, 47, 73, 0.28)',
                border: '1px solid rgba(125, 211, 252, 0.22)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.3) 0%, rgba(37, 99, 235, 0.34) 100%)'
                }
              }}
            >
              {open ? <ChevronLeftRounded sx={{ fontSize: 18 }} /> : <ChevronRightRounded sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: open ? 1.5 : 1,
          py: 2,
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha('#93c5fd', 0.45)} transparent`,
          '&::-webkit-scrollbar': {
            width: 8
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha('#93c5fd', 0.32),
            borderRadius: 999,
            border: '2px solid transparent',
            backgroundClip: 'padding-box'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha('#bfdbfe', 0.48)
          }
        }}
      >
        {menuSections.map((section) => (
          <Box key={section.label} sx={{ mb: 2.5 }}>
            {open ? (
              <ListItemButton
                onClick={() => toggleSection(section.label)}
                sx={{
                  minHeight: 44,
                  px: 1.5,
                  py: 0.6,
                  borderRadius: 3,
                  color: alpha('#ffffff', 0.82),
                  '&:hover': {
                    backgroundColor: alpha('#ffffff', 0.06)
                  }
                }}
              >
                <ListItemText
                  primary={section.label}
                  primaryTypographyProps={{
                    variant: 'overline',
                    sx: {
                      color: alpha('#ffffff', 0.52),
                      letterSpacing: '0.16em',
                      fontWeight: 700
                    }
                  }}
                />
                {expandedSections[section.label] ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
              </ListItemButton>
            ) : null}
            <Collapse in={open ? expandedSections[section.label] : true} timeout="auto" unmountOnExit={open}>
            <List disablePadding sx={{ mt: open ? 0.5 : 0 }}>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

                const button = (
                  <ListItemButton
                    component={NavLink}
                    to={item.to}
                    onClick={isMobile ? onCloseMobile : undefined}
                    sx={{
                      minHeight: 52,
                      mb: 0.5,
                      px: open ? 1.5 : 1.2,
                      py: 1,
                      borderRadius: 3,
                      justifyContent: open ? 'flex-start' : 'center',
                      color: isActive ? 'common.white' : alpha('#ffffff', 0.76),
                      backgroundColor: isActive ? alpha('#38bdf8', 0.18) : 'transparent',
                      border: `1px solid ${isActive ? alpha('#7dd3fc', 0.22) : 'transparent'}`,
                      boxShadow: isActive ? 'inset 0 0 0 1px rgba(125, 211, 252, 0.08)' : 'none',
                      '&:hover': {
                        backgroundColor: alpha('#38bdf8', 0.1),
                        color: 'common.white'
                      }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1.5 : 0,
                        justifyContent: 'center',
                        color: 'inherit'
                      }}
                    >
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    {open && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: isActive ? 700 : 500
                        }}
                      />
                    )}
                  </ListItemButton>
                );

                return (
                  <Box key={item.to}>
                    {open ? button : <Tooltip title={item.label} placement="right">{button}</Tooltip>}
                  </Box>
                );
              })}
            </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: open ? 1.5 : 1, borderTop: `1px solid ${alpha('#ffffff', 0.08)}` }}>
        <ListItemButton
          onClick={onLogout}
          sx={{
            minHeight: 52,
            px: open ? 1.5 : 1.2,
            borderRadius: 3,
            justifyContent: open ? 'flex-start' : 'center',
            color: alpha('#ffffff', 0.82),
            '&:hover': {
              backgroundColor: alpha('#ef4444', 0.16),
              color: 'common.white'
            }
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 1.5 : 0,
              color: 'inherit',
              justifyContent: 'center'
            }}
          >
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          {open && (
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontWeight: 600
              }}
            />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={drawerOpen}
      onClose={onCloseMobile}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? drawerWidth : open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isMobile ? drawerWidth : open ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          border: 'none',
          overflowX: 'hidden',
          transition: 'width 220ms ease'
        }
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
